import React, { useState, useEffect } from "react";
import { Table, Input, Tooltip } from "antd";
import { SearchOutlined, FilterFilled, CloseOutlined, DeleteOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { SortOrder, SorterResult } from 'antd/lib/table/interface';
import { SimpleSecurityDefinitionAdminDto, SimpleSecurityUserAdminDto } from "../../../services/ApiTypes.ts"
import { highlightText, usePaginationConfig, useTableScrollY, useFilteredData, matchesSearchQuery, createHandleHighlight, useSortOrder } from "../../shared/helper/TableHelper.tsx";
import './UsersTable.css';

interface UsersTableProps {
  simpleSecurityDefinition: SimpleSecurityDefinitionAdminDto | undefined,
  usersData: SimpleSecurityUserAdminDto[] | undefined,
  onRowClick :any,
  deleteUser: any
}

const UsersTable: React.FC<UsersTableProps> = ({
  simpleSecurityDefinition, 
  usersData, 
  onRowClick,
  deleteUser
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const { pagination, forceRefresh } = usePaginationConfig(usersData);
  const tableScrollY = useTableScrollY();

  const handleSearch = () => {
    filterData(); 
  };

  const { filteredData, setFilteredData } = useFilteredData(usersData);
  const [filterVisible, setFilterVisible] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const handleHighlight = createHandleHighlight(setSearchQuery);
  const { sortOrder, setSortOrder } = useSortOrder();

  const [columnQueries, setColumnQueries] = useState({ 
    name: '',
    surname: '',
    email: '',
    phoneNumber: '',
    additionalOne: '',
    additionalTwo: '',
  });

  const resetSearchAndFilters = () => {
    setSearchQuery('');
    setColumnQueries({
      name: '',
      surname: '',
      email: '',
      phoneNumber: '',
      additionalOne: '',
      additionalTwo: '',
    });
    setFilterVisible(false);
    setCurrentPage(1);
  };

  const matchesColumnFilters = (row: SimpleSecurityUserAdminDto, columnQueriesArr: typeof columnQueries) => {
    return Object.keys(columnQueriesArr).every((key) => {
      const query = columnQueriesArr[key as keyof typeof columnQueries].toLowerCase();
      if (query) {
        return row[key as keyof SimpleSecurityUserAdminDto]?.toString().toLowerCase().includes(query);
      }
      return true;
    });
  };

  const filterData = () => {
    const searchText = searchQuery.toLowerCase();
    const filtered = usersData?.filter(row => 
      matchesSearchQuery(row, searchText) && matchesColumnFilters(row, columnQueries)
    );
    setFilteredData(filtered);
  };

  const isRowValid = (record: SimpleSecurityUserAdminDto):boolean => {
    if(simpleSecurityDefinition === undefined){
      return true;
    }
    if(!isEntryValid(simpleSecurityDefinition.name, record.name)){
      return false;
    }
    if(!isEntryValid(simpleSecurityDefinition.surname, record.surname)){
      return false;
    }
    if(!isEntryValid(simpleSecurityDefinition.email, record.email)){
      return false;
    }
    if(!isEntryValid(simpleSecurityDefinition.phoneNumber, record.phoneNumber)){
      return false;
    }
    if(!isEntryValid(simpleSecurityDefinition.additionalOne, record.additionalOne)){
      return false;
    }
    if(!isEntryValid(simpleSecurityDefinition.additionalTwo, record.additionalTwo)){
      return false;
    }
    if(simpleSecurityDefinition.surname != null && (record.surname === '' || record.surname == null || record.surname === undefined )){
      return false;
    }
    return true;
  }
  const isEntryValid = (ssd: string|undefined, entry:string|undefined):boolean =>{
    if((ssd != null && ssd !== "") && (entry === '' || entry == null || entry === undefined)){
      return false;
    }
    return true;
  }

  useEffect(() => {
    filterData();
  }, [columnQueries]);

  const handleColumnQueryChange = (column: keyof typeof columnQueries, value: string) => {
    setColumnQueries(previousState => ({ ...previousState, [column]: value })); 
  };

  const columns = [
    {
      title: "",
      key: 'error',
      width: '4%',
      render: (text: string, record: SimpleSecurityUserAdminDto) => (
        <div>
          {!isRowValid(record) && (
          <Tooltip title="Required fields missing">
            <ExclamationCircleOutlined data-testid="row-error-icon" style={{ color: 'red', marginLeft: 8 }} />
          </Tooltip>
        )}</div>
      ),
    },
    {
      title: (
        <span className={simpleSecurityDefinition?.name ? '' : 'disabled-header'}>
          {simpleSecurityDefinition?.name ?simpleSecurityDefinition.name : "Name"}
        </span>
      ),
      dataIndex: 'name',
      key: 'name',
      className: 'name-column',
      width: '12%',
      sorter: (a: SimpleSecurityUserAdminDto, b: SimpleSecurityUserAdminDto) => (a.name ?? "").localeCompare(b.name ?? ""),
      sortOrder: sortOrder.columnKey === 'name' ? sortOrder.order : undefined,
      render: (text: string | number) => (
        <span className={simpleSecurityDefinition?.name ? '' : 'disabled-text'}>
          {highlightText(text, searchQuery)}
        </span>
      ),
    },
    {
      title: (
        <span className={simpleSecurityDefinition?.surname ? '' : 'disabled-header'}>
          {simpleSecurityDefinition?.surname ?simpleSecurityDefinition.surname : "Surname"}
        </span>
      ),
      dataIndex: 'surname',
      key: 'surname',
      className: 'surname-column',
      width: '12%',
      sorter: (a: SimpleSecurityUserAdminDto, b: SimpleSecurityUserAdminDto) => (a.surname ?? "").localeCompare(b.surname ?? ""),
      sortOrder: sortOrder.columnKey === 'surname' ? sortOrder.order : undefined,
      render: (text: string | number) => (
        <span className={simpleSecurityDefinition?.surname ? '' : 'disabled-text'}>
          {highlightText(text, searchQuery)}
        </span>
      ),
    },
    {
      title: (
        <span className={simpleSecurityDefinition?.email ? '' : 'disabled-header'}>
          {simpleSecurityDefinition?.email ?simpleSecurityDefinition.email : "Email"}
        </span>
      ),
      dataIndex: 'email',
      key: 'email',
      className: 'email-column',
      width: '16%',
      sorter: (a: SimpleSecurityUserAdminDto, b: SimpleSecurityUserAdminDto) => (a.email ?? "").localeCompare(b.email ?? ""),
      sortOrder: sortOrder.columnKey === 'email' ? sortOrder.order : undefined,
      render: (text: string | number) => (
        <span className={simpleSecurityDefinition?.email ? '' : 'disabled-text'}>
          {highlightText(text, searchQuery)}
        </span>
      ),
    },
    {
      title: (
        <span className={simpleSecurityDefinition?.phoneNumber ? '' : 'disabled-header'}>
          {simpleSecurityDefinition?.phoneNumber ?simpleSecurityDefinition.phoneNumber : "Phone Number"}
        </span>
      ),
      dataIndex: 'phoneNumber',
      key: 'phoneNumber',
      className: 'phoneNumber-column',
      width: '16%',
      sorter: (a: SimpleSecurityUserAdminDto, b: SimpleSecurityUserAdminDto) => (a.phoneNumber ?? "").localeCompare(b.phoneNumber ?? ""),
      sortOrder: sortOrder.columnKey === 'phoneNumber' ? sortOrder.order : undefined,
      render: (text: string | number) => (
        <span className={simpleSecurityDefinition?.phoneNumber ? '' : 'disabled-text'}>
          {highlightText(text, searchQuery)}
        </span>
      ),
    },
    {
      title: (
        <span className={simpleSecurityDefinition?.additionalOne ? '' : 'disabled-header'}>
          {simpleSecurityDefinition?.additionalOne ?simpleSecurityDefinition.additionalOne : "Additional One"}
        </span>
      ),
      dataIndex: 'additionalOne',
      key: 'additionalOne',
      className: 'additionalOne-column',
      width: '14%',
      sorter: (a: SimpleSecurityUserAdminDto, b: SimpleSecurityUserAdminDto) => (a.additionalOne ?? "").localeCompare(b.additionalOne ?? ""),
      sortOrder: sortOrder.columnKey === 'additionalOne' ? sortOrder.order : undefined,
      render: (text: string | number) => (
        <span className={simpleSecurityDefinition?.additionalOne ? '' : 'disabled-text'}>
          {highlightText(text, searchQuery)}
        </span>
      ),
    },
    {
      title: (
        <span className={simpleSecurityDefinition?.additionalTwo ? '' : 'disabled-header'}>
          {simpleSecurityDefinition?.additionalTwo ?simpleSecurityDefinition.additionalTwo : "Additional Two"}
        </span>
      ),
      dataIndex: 'additionalTwo',
      key: 'additionalTwo',
      className: 'additionalTwo-column',
      width: '14%',
      sorter: (a: SimpleSecurityUserAdminDto, b: SimpleSecurityUserAdminDto) => (a.additionalTwo ?? "").localeCompare(b.additionalTwo ?? ""),
      sortOrder: sortOrder.columnKey === 'additionalTwo' ? sortOrder.order : undefined,
      render: (text: string | number) => (
        <span className={simpleSecurityDefinition?.additionalTwo ? '' : 'disabled-text'}>
          {highlightText(text, searchQuery)}
        </span>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      className: 'action-column',
      width: '9%',
      render: (_: any, record: SimpleSecurityUserAdminDto) => (
        <div 
          onClick={(e) => {
            e.stopPropagation();
            handleDeleteUser(record);
          }} 
          className="delete-cell"
          data-testid="delete-cell"
        >
          <Tooltip title="Delete">
            <DeleteOutlined 
              className="delete-icon"
              data-testid="delete-button"
            />
          </Tooltip>
        </div>
      ),
    }
    
  ]

  const handleDeleteUser = (selectedUser: SimpleSecurityUserAdminDto) => {
    deleteUser(selectedUser);
  }

  return (
    <>
      <div className="users-table">
        <div className="icon-container">
          <Input
              placeholder = "Search"
              value = {searchQuery}
              onChange = {handleHighlight} 
              className = "search-input"
              data-testid = "search-input"
          />
          <Tooltip title = "Apply Filters">
              <SearchOutlined data-testid="apply-filters-button" className="icon-blue" onClick={() => handleSearch()}/>
          </Tooltip>
          <Tooltip title = "Toggle Advanced Filter">
              <FilterFilled data-testid="toggle-advanced-filters-button"  className = "icon-blue" onClick={() => setFilterVisible(!filterVisible)} />
          </Tooltip>
          <Tooltip title = "Clear All Filters">
              <CloseOutlined data-testid="clear-all-filters-button" className = "icon-blue" onClick={() => resetSearchAndFilters()} />
          </Tooltip>
        </div>
        {filterVisible && ( 
          <div className = "filter-container">
          <span className = "error-column" ></span>
          <Input 
              placeholder = {simpleSecurityDefinition?.name||"Name"}
              value = {columnQueries.name}
              onChange = {(eventObject) => handleColumnQueryChange('name', eventObject.target.value) }
              className = "name-column" 
              data-testid = "name-search-input"
          />
          <Input 
              placeholder = {simpleSecurityDefinition?.surname||"Surname"}
              value = {columnQueries.surname}
              onChange = {(eventObject) => handleColumnQueryChange('surname', eventObject.target.value) }
              className = "surname-column" 
              data-testid = "surname-search-input"
          />
          <Input 
              placeholder = {simpleSecurityDefinition?.email||"Email"}
              value = {columnQueries.email}
              onChange = {(eventObject) => handleColumnQueryChange('email', eventObject.target.value) }
              className = "email-column" 
              data-testid = "email-search-input"
          />
          <Input 
              placeholder = {simpleSecurityDefinition?.phoneNumber||"Phone Number"}
              value = {columnQueries.phoneNumber}
              onChange = {(eventObject) => handleColumnQueryChange('phoneNumber', eventObject.target.value) }
              className = "phoneNumber-column" 
              data-testid = "phoneNumber-search-input"
          />
          <Input 
              placeholder = {simpleSecurityDefinition?.additionalOne||"Additional One"}
              value = {columnQueries.additionalOne}
              onChange = {(eventObject) => handleColumnQueryChange('additionalOne', eventObject.target.value) }
              className = "additionalOne-column" 
              data-testid = "additionalOne-search-input"
          />
          <Input 
              placeholder = {simpleSecurityDefinition?.additionalTwo||"Additional Two"}
              value = {columnQueries.additionalTwo}
              onChange = {(eventObject) => handleColumnQueryChange('additionalTwo', eventObject.target.value) }
              className = "additionalTwo-column" 
              data-testid = "additionalTwo-search-input"
          />
          <span className = "action-column" ></span>

          </div>
          )}

          <Table 
          key = {forceRefresh}
          columns = {columns} 
          className="main-table"
          size="small"
          rowKey={record => record.id??""}
          onRow={(record, index) => ({
              onClick: () => onRowClick(record, index),
              'data-testid': `configuration-row-${record.id}`,
          })}
          scroll={{ y: tableScrollY }}
          pagination = {{
              pageSizeOptions:pagination.pageSizeOptions,
              showSizeChanger:pagination.showSizeChanger,
              defaultPageSize: pagination.defaultPageSize,
              current: currentPage,
              onChange: (page) => setCurrentPage(page),
          }}
          dataSource={filteredData as SimpleSecurityUserAdminDto[] | undefined} 
          onChange={(_, __, sorter) => {
              const { columnKey, order } = sorter as SorterResult<SimpleSecurityUserAdminDto>;
              setSortOrder({
              columnKey: columnKey ? String(columnKey) : null,
              order: order as SortOrder,
              });
          }}
          />
      </div>
    </>
  );
};

export default UsersTable;